import snakeCase from 'lodash/snakeCase';

import { asAbsoluteURL } from '@commons/helpers/url';
import { LOCALES } from '@commons/i18n';
import type { DrupalNode, DrupalView } from '@commons/types/drupal';

/**
 * Options for fetching a Drupal view.
 */

export type FetchViewOptions = {
  /**
   * The ID of the view to fetch.
   *
   * Corresponds to the machine name of the Drupal view.
   */
  id: string;

  /**
   * The display of the view to fetch.
   *
   * Corresponds to the machine name of the Drupal view display.
   */
  display: string;

  /**
   * The locale to fetch the view in.
   */
  locale: LOCALES;

  /**
   * The number of items to fetch.
   *
   * If not provided, the default setting of the view will be used.
   */
  limit?: number;

  /**
   * The number of items to skip.
   *
   * If not provided, the default setting of the view will be used.
   */
  offset?: number;

  /**
   * Filters to apply to the view.
   *
   * Corresponds to the exposed filters of the view.
   *
   * The key is the filter identifier and the value is either a string or an array of strings.
   */
  filters?: Record<string, string | string[]>;

  /**
   * An argument to pass to the view.
   *
   * Corresponds to the contextual filters of the view.
   *
   * If multiple arguments are required, they should be set like in the View preview (each value separated by a /).
   */
  argument?: string[];
};

/**
 * Fetch a Drupal view from the API.
 *
 * Unlike other services, this function can be called from client components.
 * This implies that the `fetch` function used here is the one from the browser and not the one extended by Next.js.
 * Therefore, options like `next.revalidate` are not available.
 *
 * @param options - The options for fetching the view.
 *
 * @returns A promise that resolves to the fetched view content
 */

export const fetchView = async <Content = DrupalNode>({
  id,
  display,
  locale,
  limit,
  offset,
  filters,
  argument,
}: FetchViewOptions): Promise<Content[]> => {
  const endpoint = `${locale}/api/pure/v1/view/${id}/${display}`;

  // Preparing the query parameters in order.
  const timestamp = Date.now().toString();
  const params = new URLSearchParams({ _format: 'json', timestamp });

  if (limit) params.set('limit', limit.toString());
  if (offset) params.set('offset', offset.toString());

  // Arguments are passed as an array of strings.
  if (argument) {
    argument.forEach((arg) => params.append('argument[]', arg));
  }

  // Filters values can be either a string or an array of strings.
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      // Keys are `snake cased` on the Drupal side.
      const filterKey = snakeCase(key);

      if (typeof value === 'string') {
        params.set(`exposed_filter[${filterKey}]`, value);
      } else if (Array.isArray(value)) {
        value.forEach((v) => params.append(`exposed_filter[${filterKey}][]`, v));
      }
    });
  }

  // Decoding the parameters and building the URL.
  const decodedParams = decodeURIComponent(params.toString());
  const url = asAbsoluteURL(`${endpoint}?${decodedParams}`);

  const res = await fetch(url);

  if (!res.ok) {
    throw new Error(`Failed to fetch view ${id} with display ${display}`);
  }

  const data = (await res.json()) as DrupalView<Content>;

  return data.content;
};
